import { useEffect, useRef } from 'react';
import fixtures from '../data/fixtures.json';

interface Lens {
  kind: string;
  headline: string;
  sections: { title: string; body: string }[];
}
interface DeepDive { ticker: string; lenses: Lens[] }

const deepDives = ((fixtures as any).deepDives || {}) as Record<string, DeepDive>;
const stocks = (fixtures.stocks || []) as { ticker: string; name: string }[];

/**
 * The demo's Deep Dive. Two things it does deliberately differently from the Mac app:
 *
 *   1. The reports are PRE-BAKED, composed at fixture-generation time from each stock's own
 *      numbers. A public page can't make live model calls — the key would either sit in the
 *      browser where anyone can read it, or behind a proxy any visitor could run up a bill on.
 *      The banner below says so plainly rather than implying a model wrote this on demand.
 *
 *   2. Typography follows the Mac app's readability fix: body prose at full reading size with
 *      real line spacing, not the caption-sized text this surface originally shipped with.
 */
export default function DeepDiveModal({
  ticker, onClose,
}: {
  ticker: string;
  onClose: () => void;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);

  // Escape to dismiss, and focus moves into the dialog so keyboard users aren't stranded
  // behind it. Cheap to do, and a modal without either is genuinely hard to use.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    closeRef.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const dive = deepDives[ticker];
  const stock = stocks.find((s) => s.ticker === ticker);
  if (!dive || !stock) return null;

  return (
    <div className="modalBackdrop" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={`Deep dive for ${stock.name}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modalHead">
          <div>
            <div className="modalTitle">Deep dive: {stock.name}</div>
            <div className="modalTicker">{ticker}</div>
          </div>
          <button ref={closeRef} className="reset" onClick={onClose}>Close</button>
        </div>

        <div className="sampleNote">
          <strong>Sample report.</strong> In the Mac app each lens is generated by Claude on
          demand, with the Research lens performing live web research and citing sources. These
          are composed from the fixture data instead — a public demo can't spend API calls on
          behalf of anonymous visitors.
        </div>

        <div className="modalBody">
          {dive.lenses.map((lens) => (
            <section key={lens.kind} className="lens">
              <h3>{lens.kind}</h3>
              <p className="lensHead">{lens.headline}</p>
              {lens.sections.map((s) => (
                <div className="lensSection" key={s.title}>
                  <h4>{s.title}</h4>
                  <p>{s.body}</p>
                </div>
              ))}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
